import { PARENT_FORM_CATEGORY_SLUG } from "Constants/constant";

export const mapCategoryOption = (category) => ({
  ...category,
  label: category?.label ?? category?.name,
  value: category?.value ?? category?.id,
});

export const extractCreatedFormCategory = (response) => {
  const raw = response?.form_category;
  const category =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? raw.data && !Array.isArray(raw.data)
        ? raw.data
        : raw
      : response?.data?.form_category ?? null;

  if (!category || Array.isArray(category)) {
    return null;
  }

  const mapped = mapCategoryOption(category);
  if (mapped.id == null && mapped.value == null) {
    return null;
  }

  return mapped;
};

export const extractFormCategoriesFromResponse = (response) => {
  if (!response) {
    return [];
  }
  if (Array.isArray(response)) {
    return response;
  }
  const raw =
    response.form_categories ??
    response.data?.form_categories ??
    response.data?.data?.form_categories;
  if (Array.isArray(raw)) {
    return raw;
  }
  if (raw && Array.isArray(raw.data)) {
    return raw.data;
  }
  return [];
};

/** Select options must use numeric `id` as value (SIL API may send name as `value`). */
export const normalizeFormCategoryOptions = (categories = []) =>
  categories.map((category) => {
    const id = category?.id ?? null;
    return {
      ...category,
      label: category?.name ?? category?.label,
      value: id,
      id,
    };
  });

export const resolveFormCategoryOptionForTemplate = (
  templateData,
  categories,
  role = "crm"
) => {
  if (!templateData) {
    return null;
  }

  const categoryName =
    templateData?.form_category_with_name?.name ??
    templateData?.category_name ??
    null;

  const categoryId =
    role === "sil"
      ? templateData?.form_category_with_name?.id ??
        templateData?.category_id ??
        templateData?.form_category_id
      : templateData?.form_category_id ??
        templateData?.form_category_with_name?.id ??
        templateData?.category_id;

  if (categoryName && categories.length > 0) {
    const matchedByName = categories.find((option) =>
      categoryNamesMatch(option.label ?? option.name, categoryName)
    );
    if (matchedByName) {
      return matchedByName;
    }
  }

  if (categoryId == null) {
    return null;
  }

  const matched = categories.find(
    (option) =>
      option.id === categoryId ||
      option.value === categoryId ||
      Number(option.value) === Number(categoryId)
  );
  if (matched) {
    return matched;
  }

  if (categoryName) {
    return {
      id: categoryId,
      value: categoryId,
      label: categoryName,
      name: categoryName,
    };
  }

  return null;
};

export const resolveFormCategoryIdFromOption = (option) => {
  const raw = option?.id ?? option?.value;
  if (raw == null || raw === "") {
    return null;
  }
  const numeric = Number(raw);
  return Number.isNaN(numeric) ? null : numeric;
};

export const isCustomFormCategory = (category) =>
  Boolean(category?.is_custom ?? category?.sp_id);

const PLATFORM_CRM_FORM_CATEGORY_SLUGS = new Set(
  Object.values(PARENT_FORM_CATEGORY_SLUG)
    .map((entry) => entry?.SLUG)
    .filter(Boolean)
    .map((slug) => String(slug).toLowerCase())
);

/** Tenant-defined CRM category (not a built-in platform slug). */
export const isCustomFormCategorySlug = (slug) => {
  const normalized = String(slug ?? "")
    .trim()
    .toLowerCase();
  if (!normalized) {
    return false;
  }
  return !PLATFORM_CRM_FORM_CATEGORY_SLUGS.has(normalized);
};

export const splitFormCategories = (categories = []) => {
  const platform = [];
  const custom = [];

  categories.forEach((category) => {
    if (isCustomFormCategory(category)) {
      custom.push(category);
    } else {
      platform.push(category);
    }
  });

  return { platform, custom };
};

export const isPlatformOtherFormCategory = (category) => {
  if (!category || isCustomFormCategory(category)) {
    return false;
  }
  const slug = String(category.slug ?? "").toLowerCase();
  const name = normalizeCategoryName(category.name ?? category.label).toLowerCase();
  return (
    slug === "other" ||
    slug === "others" ||
    slug.endsWith("-other") ||
    name === "other" ||
    name === "others"
  );
};

/** Platform order through Other (inclusive), then custom, then any platform items after Other. */
export const orderFormCategoriesCustomAfterOther = (categories = []) => {
  const { platform, custom } = splitFormCategories(categories);
  const otherIndex = platform.findIndex(isPlatformOtherFormCategory);
  if (otherIndex === -1) {
    return [...platform, ...custom];
  }
  return [
    ...platform.slice(0, otherIndex + 1),
    ...custom,
    ...platform.slice(otherIndex + 1),
  ];
};

/** Insert tab/dropdown children immediately after the platform "Other" entry (by tab id). */
export const insertFormTabChildrenAfterOther = (
  staticChildren,
  otherTabId,
  insertChildren
) => {
  if (!insertChildren?.length) {
    return staticChildren;
  }
  const otherIndex = staticChildren.findIndex((child) => child.id === otherTabId);
  if (otherIndex === -1) {
    return [...staticChildren, ...insertChildren];
  }
  return [
    ...staticChildren.slice(0, otherIndex + 1),
    ...insertChildren,
    ...staticChildren.slice(otherIndex + 1),
  ];
};

export const normalizeCategoryName = (name) => name?.trim() ?? "";

export const categoryNamesMatch = (left, right) =>
  normalizeCategoryName(left).toLowerCase() ===
  normalizeCategoryName(right).toLowerCase();

/** Whether a category belongs to the CRM, HRM, or Home MGT module being edited. */
export const categoryAppliesToFormModule = (category, moduleFor) => {
  if (moduleFor === "sil") {
    const silModule = String(category?.for ?? category?.module ?? "").toLowerCase();
    if (silModule) {
      return silModule === "sil";
    }
    const silSlug = String(category?.slug ?? "").toLowerCase();
    return !silSlug.includes("-crm-") && !silSlug.includes("-hrm-");
  }

  const module = moduleFor === "hrm" ? "hrm" : "crm";
  const raw = category?.for ?? category?.module;
  if (raw) {
    const normalized = String(raw).toLowerCase();
    if (normalized === "both") {
      return true;
    }
    return normalized === module;
  }

  const slug = String(category?.slug ?? "").toLowerCase();
  if (slug.includes("-crm-") || slug.includes("-hrm-")) {
    return slug.includes(`-${module}-`);
  }

  // Legacy tenant slugs (sp-{id}-{name}) were CRM-first; do not block HRM creates.
  return module === "crm";
};

/** Resolve category ids for list filters; null means no category filter (show all). */
export const resolveFormCategoryFilterIds = ({
  explicitSelection,
  persistedSelection,
}) => {
  if (explicitSelection !== undefined && explicitSelection !== "both") {
    if (
      !explicitSelection ||
      !Array.isArray(explicitSelection) ||
      explicitSelection.length === 0
    ) {
      return null;
    }

    return explicitSelection.map((item) => item.id ?? item);
  }

  if (Array.isArray(persistedSelection) && persistedSelection.length > 0) {
    return persistedSelection.map((item) => item.id ?? item);
  }

  return null;
};

export const buildFormTemplateStatusFilter = (status) => {
  if (status === "hidden") {
    return { is_hidden: true };
  }

  if (status === "publish" || status === "draft") {
    return { is_hidden: false };
  }

  return {};
};

export const resolveFormTemplateRowStatus = (template, activeStatus) => {
  if (activeStatus && activeStatus !== "all") {
    return activeStatus;
  }

  if (template?.form_status) {
    return template.form_status;
  }

  if (template?.is_hidden) {
    return "hidden";
  }

  return "publish";
};

const FORM_SCOPE_META = {
  crm: {
    label: "CRM",
    subtitle: "Participant form templates",
  },
  hrm: {
    label: "HRM",
    subtitle: "Team and worker form templates",
  },
  sil: {
    label: "Home MGT",
    subtitle: "SIL accommodation form templates",
  },
};

export const getFormScopeMeta = (routeRole = "crm") =>
  FORM_SCOPE_META[routeRole] ?? {
    label: String(routeRole || "Forms").toUpperCase(),
    subtitle: "All your forms in one place",
  };

/** Maps route `role` props (crm, hrm, sil, worker, sp/hrm, …) to badge scope keys. */
export const resolveFormScopeFromRole = (role) => {
  const normalized = String(role || "")
    .toLowerCase()
    .replace(/^sp\/?/, "");

  if (normalized === "sil") {
    return "sil";
  }
  if (normalized === "hrm" || normalized === "worker") {
    return "hrm";
  }
  return "crm";
};
